import { Suspense, useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useSearchParams } from 'react-router-dom';
import { metricKeyAtom } from '@/atoms/metric';
import { unwrappedSelectedPlayerNameAtom } from '@/atoms/player/info';
import { playerIdAtom } from '@/atoms/player/player';
import { METRIC_KEY, PLAYER_ID } from '@/consts/searchKeys';
import {
  playerEventMarkersDescriptionsAtom,
  playerEventMarkersForMetricAtom,
} from '@/atoms/queries/player/playerEventData';
import { useSetPlayerEventDataDebouncedFilterParams } from '@/hooks/apiFilterParams/useSetPlayerEventDataDebouncedFilterParams';
import { useFormatVideoEvents } from '@/hooks/useFormatVideoEvents';
import {
  EntityVideoPlayerPanelHeader,
  VideoPlayerPanelBodyBase,
  VideoPlayerPanelHeaderBase,
} from '@/components/video/VideoPlayerPanel';
import { Card } from '@statsbomb/kitbag-components';
import { useVideoDataFromEventMarkers } from '@/hooks/useVideoDataFromEventMarkers';

const PlayerVideoPlayerPanelHeaderWithData = () => (
  <EntityVideoPlayerPanelHeader entityNameAtom={unwrappedSelectedPlayerNameAtom} entityIdParam={PLAYER_ID} />
);

const PlayerVideoPlayerPanelHeader = () => (
  <Suspense fallback={<VideoPlayerPanelHeaderBase isLoading />}>
    <PlayerVideoPlayerPanelHeaderWithData />
  </Suspense>
);

const PlayerVideoPlayerPanelBodyWithData = () => {
  useSetPlayerEventDataDebouncedFilterParams();
  const [searchParams] = useSearchParams();
  const metricKeyParam = searchParams.get(METRIC_KEY);
  const playerIdParam = searchParams.get(PLAYER_ID);
  const { videoIds, unavailableVideosCount } = useVideoDataFromEventMarkers(playerEventMarkersForMetricAtom);
  const videoEventMarkersDescriptions = useAtomValue(playerEventMarkersDescriptionsAtom);
  const videoEvents = useFormatVideoEvents(videoIds, videoEventMarkersDescriptions);

  const setMetricKey = useSetAtom(metricKeyAtom);
  const setPlayerId = useSetAtom(playerIdAtom);

  useEffect(() => {
    if (playerIdParam && metricKeyParam) {
      setPlayerId(Number(playerIdParam));
      setMetricKey(metricKeyParam);
    }
  }, [playerIdParam, metricKeyParam]);

  return (
    <VideoPlayerPanelBodyBase
      isLoading={!playerIdParam || !metricKeyParam}
      videoEvents={videoEvents}
      unavailableVideosCount={unavailableVideosCount}
    />
  );
};

const PlayerVideoPlayerPanelBody = () => (
  <Suspense fallback={<VideoPlayerPanelBodyBase videoEvents={[]} isLoading />}>
    <PlayerVideoPlayerPanelBodyWithData />
  </Suspense>
);

export const PlayerDataLockerVideoPreview = () => (
  <Card>
    <PlayerVideoPlayerPanelHeader />
    <PlayerVideoPlayerPanelBody />
  </Card>
);
